import { IamSetupTabs } from './IamSetupTabs'
import { MANAGED_POLICY_ARNS, READONLY_NAME, waToolCliStep } from './iamSetup'

/**
 * Per-account setup help: create a dedicated read-only IAM user + access key
 * in ONE account, with the read-only AWS-managed policies attached. Used in
 * per-account mode, where the consultant pastes a key pair for each account.
 *
 * Org-wide setup (assume-role across many accounts) is a different flow —
 * see AwsOrgIamSetupHelp.
 */

const USER_NAME = READONLY_NAME

// Render the managed-policy list into each tool's syntax from one source so the
// four snippets can't drift.
const cfnManaged = MANAGED_POLICY_ARNS.map((a) => `        - ${a}`).join('\n')
const tfAttach = MANAGED_POLICY_ARNS.map(
  (a, i) => `resource "aws_iam_user_policy_attachment" "p${i}" {
  user       = aws_iam_user.assessment.name
  policy_arn = "${a}"
}`,
).join('\n\n')
const cliAttach = MANAGED_POLICY_ARNS.map(
  (a) => `aws iam attach-user-policy --user-name ${USER_NAME} \\
  --policy-arn ${a}`,
).join('\n')

const CLOUDFORMATION = `AWSTemplateFormatVersion: '2010-09-09'
Description: Read-only IAM user + access key for the AI Assessment import
Resources:
  AssessmentUser:
    Type: AWS::IAM::User
    Properties:
      UserName: ${USER_NAME}
      ManagedPolicyArns:
${cfnManaged}
  AssessmentAccessKey:
    Type: AWS::IAM::AccessKey
    Properties:
      UserName: !Ref AssessmentUser
Outputs:
  AccessKeyId:
    Value: !Ref AssessmentAccessKey
  SecretAccessKey:
    Value: !GetAtt AssessmentAccessKey.SecretAccessKey

# Deploy, then read the key pair back out of the stack outputs:
#   aws cloudformation deploy --template-file ai-assessment-readonly.yaml \\
#     --stack-name ${USER_NAME} --capabilities CAPABILITY_NAMED_IAM
#   aws cloudformation describe-stacks --stack-name ${USER_NAME} \\
#     --query "Stacks[0].Outputs" --output table`

const TERRAFORM = `resource "aws_iam_user" "assessment" {
  name = "${USER_NAME}"
}

${tfAttach}

resource "aws_iam_access_key" "assessment" {
  user = aws_iam_user.assessment.name
}

output "access_key_id" {
  value = aws_iam_access_key.assessment.id
}

output "secret_access_key" {
  value     = aws_iam_access_key.assessment.secret
  sensitive = true
}

# terraform apply
# terraform output -raw access_key_id
# terraform output -raw secret_access_key`

const CLI = `aws iam create-user --user-name ${USER_NAME}

# Attach the read-only AWS-managed policies.
${cliAttach}

# Prints AccessKeyId + SecretAccessKey once — copy them now.
aws iam create-access-key --user-name ${USER_NAME}
${waToolCliStep('user', USER_NAME)}`

const CLOUDSHELL = `# Open AWS CloudShell from the console toolbar (>_ icon, top-right) while
# signed in as an admin — it's pre-authenticated, so no keys to configure.
# Then paste the same commands as the AWS CLI tab:

${CLI}`

const SNIPPETS = {
  cloudformation: CLOUDFORMATION,
  terraform: TERRAFORM,
  cli: CLI,
  cloudshell: CLOUDSHELL,
}

export function AwsIamSetupHelp() {
  return (
    <IamSetupTabs
      testIdPrefix="aws-iam-help"
      triggerLabel="How do I create the access key? (read-only IAM user)"
      modalTitle="Create a read-only access key"
      snippets={SNIPPETS}
      intro={
        <>
          Create a dedicated <strong>read-only</strong> IAM user and one access key in this account,
          with the AWS-managed <code>ReadOnlyAccess</code> + <code>AWSBillingReadOnlyAccess</code>{' '}
          policies attached (the scan only ever calls Describe/List/Get). For a Well-Architected
          Review, the CLI tabs include an optional step that also lets us save the finished review
          to your Well-Architected Tool. Pick your tool:
        </>
      }
      footer={
        <>
          Paste the resulting <strong>Access key ID</strong> + <strong>Secret access key</strong> into
          the account's key fields. We encrypt them at rest and only ever call read-only APIs.
        </>
      }
    />
  )
}
